/*
 *  -----------------------------------------------------------------------
 *    <copyright file="funnel/display.tsx" company="Triumph Research Intelligence">
 *        Copyright (C) 2019 by Triumph Research Intelligence. All rights reserved.
 *    </copyright>
 *   -----------------------------------------------------------------------
 *
 */

import React from 'react';
import {
    FormatNumberForChart,
    HC_RAG_NAMES,
    HC_COLORS_STANDARD,
    HC_THRESHOLD_NAMES,
    IOpraChartPlot,
    IOpraChartSeries,
    OpraChartLineStyle,
    OpraChartSeriesType,
    OpraColours,
    OpraMarkerShape,
    OpraRag,
    OpraVerticalAlign,
} from 'interfaces/opra-chart.interface';
import { PanelIntervalType } from 'types/graphql-global-types';
import { formatKey } from 'views/components/central-monitoring/panels/panel-display';
import ScatterLineChart from 'views/components/core/charts/scatter-line-chart';

const getFunnel = ({ getRef, setLoadingComplete, panelState, sites, siteId }: any) => {
    if (!panelState?.panelData?.panelData?.length) {
        if (setLoadingComplete) setLoadingComplete();

        return <span>No data</span>;
    }

    const tooltipShowSeriesLabel = true;
    const tooltipShowSeriesThreshold = false;
    const siteSeries: IOpraChartSeries[] = [OpraRag.Green, OpraRag.Amber, OpraRag.Red].map(
        (rag: OpraRag): IOpraChartSeries => ({
            name: HC_THRESHOLD_NAMES[rag],
            type: OpraChartSeriesType.Scatter,
            style: {
                color: HC_COLORS_STANDARD[rag],
                marker: {
                    shape: OpraMarkerShape.Circle,
                    isHollow: false,
                },
                lineStyle: OpraChartLineStyle.Solid,
            },
            hasConfidences: false,
            data:
                panelState.panelData.panelData[0].points
                    ?.filter((p: any) => p.color === rag)
                    ?.map(
                        (p: any): IOpraChartPlot => ({
                            x: p.x,
                            y: p.y,
                            rag,
                            direction: p.direction,
                            score: +p.score.toFixed(2),
                            site: sites[p.siteId]?.Fields['Site.name'],
                            selected: siteId === p.siteId,
                            hideTooltip: !siteId ? undefined : siteId !== p.siteId,
                        }),
                    ) ?? [],
        }),
    );
    if (panelState.modelConfig.confidenceIntervalType === 'EXACT') {
        siteSeries.push({
            name: 'Benchmark',
            data:
                panelState.panelData.panelData[0].benchmarkLine?.map(
                    (p: any): IOpraChartPlot => ({
                        x: p.x,
                        y: p.y,
                    }),
                ) ?? [],
            style: {
                color: HC_COLORS_STANDARD[OpraColours.Black],
                lineStyle: OpraChartLineStyle.Dashed,
            },
            showInLegend: false,
            showInTooltip: false,
            type: OpraChartSeriesType.Line,
            hasConfidences: false,
        });
    }
    const secondKey: any = [
        [
            {
                label: panelState.modelConfig.benchmarkType,
                colour: OpraColours.Black,
                value: panelState?.panelData?.panelData[0]?.benchmark ?? 0,
            },
        ],
    ];
    if (!(panelState.modelConfig?.hideUpper ?? false)) {
        siteSeries.push(
            {
                name: `Upper ${HC_RAG_NAMES[OpraRag.Amber]} Threshold`,
                type:
                    panelState.modelConfig.confidenceIntervalType === 'EXACT'
                        ? OpraChartSeriesType.Line
                        : OpraChartSeriesType.SmoothLine,
                style: {
                    color: HC_COLORS_STANDARD[OpraRag.Amber],
                    lineStyle: OpraChartLineStyle.Dashed,
                },
                hasConfidences: false,
                data: panelState.panelData.panelData[0].upperLimitAmberPoints?.map((p: any) => p) ?? [],
                showInLegend: false,
                showInTooltip: false,
            },
            {
                name: `Upper ${HC_RAG_NAMES[OpraRag.Red]} Threshold`,
                type:
                    panelState.modelConfig.confidenceIntervalType === 'EXACT'
                        ? OpraChartSeriesType.Line
                        : OpraChartSeriesType.SmoothLine,
                style: {
                    color: HC_COLORS_STANDARD[OpraRag.Red],
                    lineStyle: OpraChartLineStyle.Dashed,
                },
                hasConfidences: false,
                data: panelState.panelData.panelData[0].upperLimitRedPoints?.map((p: any) => p) ?? [],
                showInLegend: false,
                showInTooltip: false,
            },
        );
        secondKey.push([
            {
                sectionPrefix: 'Upper Thresholds',
                label: FormatNumberForChart(panelState.modelConfig.upperRed),
                colour: OpraColours.Red,
            },
            {
                label: FormatNumberForChart(panelState.modelConfig.upperAmber),
                colour: OpraColours.Amber,
            },
        ]);
    }
    if (!(panelState.modelConfig?.hideLower ?? false)) {
        siteSeries.push(
            {
                name: `Lower ${HC_THRESHOLD_NAMES[OpraRag.Amber]} Threshold`,
                type:
                    panelState.modelConfig.confidenceIntervalType === 'EXACT'
                        ? OpraChartSeriesType.Line
                        : OpraChartSeriesType.SmoothLine,
                style: {
                    color: HC_COLORS_STANDARD[OpraRag.Amber],
                    lineStyle: OpraChartLineStyle.Dashed,
                },
                hasConfidences: false,
                data: panelState.panelData.panelData[0].lowerLimitAmberPoints?.map((p: any) => p) ?? [],
                showInLegend: false,
                showInTooltip: false,
            },
            {
                name: `Lower ${HC_RAG_NAMES[OpraRag.Red]} Threshold`,
                type:
                    panelState.modelConfig.confidenceIntervalType === 'EXACT'
                        ? OpraChartSeriesType.Line
                        : OpraChartSeriesType.SmoothLine,
                style: {
                    color: HC_COLORS_STANDARD[OpraRag.Red],
                    lineStyle: OpraChartLineStyle.Dashed,
                },
                hasConfidences: false,
                data: panelState.panelData.panelData[0].lowerLimitRedPoints?.map((p: any) => p) ?? [],
                showInLegend: false,
                showInTooltip: false,
            },
        );
        secondKey.push([
            {
                sectionPrefix: 'Lower Thresholds',
                label: FormatNumberForChart(panelState.modelConfig.lowerRed),
                colour: OpraColours.Red,
            },
            {
                label: FormatNumberForChart(panelState.modelConfig.lowerAmber),
                colour: OpraColours.Amber,
            },
        ]);
    }

    return (
        <ScatterLineChart
            getRef={getRef}
            plotTitle={panelState.chartConfig.plotTitle}
            xAxis={{ title: panelState.chartConfig.xTitle, isDateRange: false }}
            yAxisTitle={panelState.chartConfig.yTitle}
            showLegend={panelState.hideLegend ? !panelState.hideLegend : true}
            secondaryKey={{
                text: formatKey(secondKey),
                position: OpraVerticalAlign.bottom,
            }}
            series={siteSeries}
            horizontalLines={
                panelState.modelConfig.confidenceIntervalType === 'EXACT'
                    ? undefined
                    : [
                          {
                              name: '',
                              style: OpraChartLineStyle.Dashed,
                              color: HC_COLORS_STANDARD[OpraColours.Black],
                              y: panelState.panelData.panelData[0].benchmark ?? 0,
                          },
                      ]
            }
            tooltipShowSeriesLabel={tooltipShowSeriesLabel}
            tooltipShowSeriesThreshold={tooltipShowSeriesThreshold}
            isCustomLegendWidth={false}
            hidePlotTitle={panelState.hidePlotTitle}
        />
    );
};

const getFunnelTrend = ({ getRef, panelState, sites, siteId, study, setLoadingComplete }: any) => {
    if (!panelState?.panelData?.panelData?.length) {
        if (setLoadingComplete) setLoadingComplete();

        return <span>No data</span>;
    }

    const tooltipShowSeriesLabel = true;
    const tooltipShowSeriesThreshold = false;
    const isExactConfidence: boolean = panelState?.modelConfig?.confidenceIntervalType === PanelIntervalType.EXACT;

    const trendLine: IOpraChartSeries = siteId
        ? {
              name: sites[siteId]?.Fields['Site.name'],
              showInLegend: true,
              hasConfidences: false,
              type: OpraChartSeriesType.Line,
              style: {
                  lineStyle: OpraChartLineStyle.Solid,
                  marker: {
                      shape: OpraMarkerShape.Circle,
                      isHollow: false,
                  },
                  color: HC_COLORS_STANDARD[OpraColours.DarkBlue],
              },
              data: panelState.panelData.panelData[0].triTrendSites
                  ? (panelState.panelData.panelData[0].triTrendSites[siteId] ?? []).map(
                        (plot: any): IOpraChartPlot => ({
                            x: isNaN(plot.x) ? 0 : plot.x,
                            y: isNaN(plot.y) ? 0 : plot.y,
                            selected: plot.siteId === siteId,
                        }),
                    )
                  : [],
          }
        : {
              name: study?.studyNumber ?? 'Study',
              showInLegend: true,
              hasConfidences: false,
              type: OpraChartSeriesType.Line,
              style: {
                  lineStyle: OpraChartLineStyle.Solid,
                  marker: {
                      shape: OpraMarkerShape.Circle,
                      isHollow: false,
                  },
                  color: HC_COLORS_STANDARD[OpraColours.DarkBlue],
              },
              data: (panelState.panelData.panelData[0].triTrendStudy ?? []).map(
                  (plot: any): IOpraChartPlot => ({
                      x: isNaN(plot.x) ? 0 : plot.x,
                      y: isNaN(plot.y) ? 0 : plot.y,
                  }),
              ),
          };

    const maxX = Math.max(...trendLine.data.map((p) => Number(p.x)));

    const lowerLimitRedPoints = siteId
        ? panelState.panelData.panelData[0].lowerLimitRedPoints ?? []
        : panelState.panelData.panelData[0].studyLowerLimitRedPoints ?? [];

    const lowerLimitAmberPoints = siteId
        ? panelState.panelData.panelData[0].lowerLimitAmberPoints ?? []
        : panelState.panelData.panelData[0].studyLowerLimitAmberPoints ?? [];

    const upperLimitRedPoints = siteId
        ? panelState.panelData.panelData[0].upperLimitRedPoints ?? []
        : panelState.panelData.panelData[0].studyUpperLimitRedPoints ?? [];

    const upperLimitAmberPoints = siteId
        ? panelState.panelData.panelData[0].upperLimitAmberPoints ?? []
        : panelState.panelData.panelData[0].studyUpperLimitAmberPoints ?? [];
    const seriesData: IOpraChartSeries[] = [];
    const secondKey: any = [
        [
            {
                label: panelState.modelConfig.benchmarkType,
                colour: OpraColours.Black,
                value: siteId
                    ? panelState?.panelData?.panelData[0]?.benchmark ?? 0
                    : panelState.panelData?.panelData[0]?.studyBenchmark ?? 0,
            },
        ],
    ];
    if (!(panelState.modelConfig?.hideUpper ?? false)) {
        seriesData.push(
            {
                name: `Upper ${HC_RAG_NAMES[OpraRag.Amber]} Threshold`,
                type:
                    panelState.modelConfig.confidenceIntervalType === 'EXACT'
                        ? OpraChartSeriesType.Line
                        : OpraChartSeriesType.SmoothLine,
                style: {
                    color: HC_COLORS_STANDARD[OpraRag.Amber],
                    lineStyle: OpraChartLineStyle.Dashed,
                },
                hasConfidences: false,
                data:
                    upperLimitAmberPoints
                        ?.filter(
                            (point: any) =>
                                !point.y.toString().endsWith('Infinity') &&
                                Number(isNaN(point.x) ? 0 : point.x) <= maxX,
                        )
                        ?.map((point: any) => ({
                            x: isNaN(point.x) ? 0 : point.x,
                            y: isNaN(point.y) ? 0 : point.y,
                        })) ?? [],
                showInLegend: false,
                showInTooltip: false,
            },
            {
                name: `Upper ${HC_RAG_NAMES[OpraRag.Red]} Threshold`,
                type:
                    panelState.modelConfig.confidenceIntervalType === 'EXACT'
                        ? OpraChartSeriesType.Line
                        : OpraChartSeriesType.SmoothLine,
                style: {
                    color: HC_COLORS_STANDARD[OpraRag.Red],
                    lineStyle: OpraChartLineStyle.Dashed,
                },
                hasConfidences: false,
                data:
                    upperLimitRedPoints
                        ?.filter(
                            (point: any) =>
                                !point.y.toString().endsWith('Infinity') &&
                                Number(isNaN(point.x) ? 0 : point.x) <= maxX,
                        )
                        ?.map((point: any) => ({
                            x: isNaN(point.x) ? 0 : point.x,
                            y: isNaN(point.y) ? 0 : point.y,
                        })) ?? [],
                showInLegend: false,
                showInTooltip: false,
            },
        );
        secondKey.push([
            {
                sectionPrefix: 'Upper Thresholds',
                label: FormatNumberForChart(panelState.modelConfig.upperRed),
                colour: OpraColours.Red,
            },
            {
                label: FormatNumberForChart(panelState.modelConfig.upperAmber),
                colour: OpraColours.Amber,
            },
        ]);
    }
    if (!(panelState.modelConfig?.hideLower ?? false)) {
        seriesData.push(
            {
                name: `Lower ${HC_RAG_NAMES[OpraRag.Amber]} Threshold`,
                type:
                    panelState.modelConfig.confidenceIntervalType === 'EXACT'
                        ? OpraChartSeriesType.Line
                        : OpraChartSeriesType.SmoothLine,
                style: {
                    color: HC_COLORS_STANDARD[OpraRag.Amber],
                    lineStyle: OpraChartLineStyle.Dashed,
                },
                hasConfidences: false,
                data:
                    lowerLimitAmberPoints
                        ?.filter(
                            (point: any) =>
                                !point.y.toString().endsWith('Infinity') &&
                                Number(isNaN(point.x) ? 0 : point.x) <= maxX,
                        )
                        ?.map((point: any) => ({
                            x: isNaN(point.x) ? 0 : point.x,
                            y: isNaN(point.y) ? 0 : point.y,
                        })) ?? [],
                showInLegend: false,
                showInTooltip: false,
            },
            {
                name: `Lower ${HC_RAG_NAMES[OpraRag.Red]} Threshold`,
                type:
                    panelState.modelConfig.confidenceIntervalType === 'EXACT'
                        ? OpraChartSeriesType.Line
                        : OpraChartSeriesType.SmoothLine,
                style: {
                    color: HC_COLORS_STANDARD[OpraRag.Red],
                    lineStyle: OpraChartLineStyle.Dashed,
                },
                hasConfidences: false,
                data:
                    lowerLimitRedPoints
                        ?.filter(
                            (point: any) =>
                                !point.y.toString().endsWith('Infinity') &&
                                Number(isNaN(point.x) ? 0 : point.x) <= maxX,
                        )
                        ?.map((point: any) => ({
                            x: isNaN(point.x) ? 0 : point.x,
                            y: isNaN(point.y) ? 0 : point.y,
                        })) ?? [],
                showInLegend: false,
                showInTooltip: false,
            },
        );
        secondKey.push([
            {
                sectionPrefix: 'Lower Thresholds',
                label: FormatNumberForChart(panelState.modelConfig.lowerRed),
                colour: OpraColours.Red,
            },
            {
                label: FormatNumberForChart(panelState.modelConfig.lowerAmber),
                colour: OpraColours.Amber,
            },
        ]);
    }

    if (panelState.modelConfig.confidenceIntervalType === 'EXACT') {
        const benchmarkLine = siteId
            ? panelState.panelData?.panelData[0]?.benchmarkLine ?? []
            : panelState.panelData?.panelData[0]?.studyBenchmarkLine ?? [];

        seriesData.push({
            name: 'Benchmark',
            data:
                benchmarkLine
                    ?.filter((p: any) => Number(isNaN(p.x) ? 0 : p.x) <= maxX)
                    ?.map(
                        (p: any): IOpraChartPlot => ({
                            x: isNaN(p.x) ? 0 : p.x,
                            y: isNaN(p.y) ? 0 : p.y,
                        }),
                    ) ?? [],
            style: {
                color: HC_COLORS_STANDARD[OpraColours.Black],
                lineStyle: OpraChartLineStyle.Dashed,
            },
            showInLegend: false,
            showInTooltip: false,
            type: OpraChartSeriesType.Line,
            hasConfidences: false,
        });
    }

    return (
        <ScatterLineChart
            hidePlotTitle={panelState.hidePlotTitle}
            getRef={getRef}
            plotTitle={panelState.chartConfig.plotTitle}
            xAxis={{ title: panelState.chartConfig.xTitle, isDateRange: false }}
            yAxisTitle={panelState.chartConfig.yTitle}
            minY={isExactConfidence ? undefined : -1}
            maxY={isExactConfidence ? undefined : 1}
            showLegend={panelState.hideLegend ? !panelState.hideLegend : true}
            secondaryKey={{
                text: formatKey(secondKey),
                position: OpraVerticalAlign.bottom,
            }}
            horizontalLines={
                panelState.modelConfig.confidenceIntervalType === 'APPROXIMATE'
                    ? [
                          {
                              y: siteId
                                  ? panelState.panelData.panelData[0].benchmark
                                  : panelState.panelData.panelData[0].studyBenchmark,
                              name: '',
                              color: HC_COLORS_STANDARD[OpraColours.Black],
                              style: OpraChartLineStyle.Dashed,
                          },
                      ]
                    : undefined
            }
            series={[...seriesData, trendLine]}
            tooltipShowSeriesLabel={tooltipShowSeriesLabel}
            tooltipShowSeriesThreshold={tooltipShowSeriesThreshold}
            isCustomLegendWidth={false}
        />
    );
};

export { getFunnel, getFunnelTrend };
